<template>
  <div :class="$style.container">
    <div :class="$style.content">
      <NotificationWrap />
      <router-view v-if="isAppReady" />
      <div v-else :class="$style.loader">
        <CircleLoader />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useUserStore } from '@youandco/stores/userStore'
import { useLocalizationStore } from '@stores/localizationStore'
import { useGlobalStore } from '@stores/globalStore'

import { LanguageEnum } from '@models/entities/languages'

import { queryController } from '@composables/queryController'

import { isAvailableLocalStorage } from '@scripts/helpers/windowHelpers'
import { createLanguageTranslationsRequestUrl } from '@scripts/helpers/appHelpers'

import { LANGUAGE_MAP } from '@definitions/languages'

import CircleLoader from '@components/loaders/CircleLoader.vue'
import NotificationWrap from '@notifications/components/NotificationWrap.vue'

const router = useRouter()
const userStore = useUserStore()
const globalStore = useGlobalStore()
const localizationStore = useLocalizationStore()

const { getQueryByKey } = queryController()

const isCheckedAuthorization = computed(() => userStore.isCheckedAuth)

const isReceivedDefaultLanguageTranslations = computed(() => localizationStore.isReceivedDefaultLanguageTranslations)
const isAppReady = computed(() =>
  isReceivedDefaultLanguageTranslations.value && isCheckedAuthorization.value
)

const setLanguageToLocalStorage = (language: LanguageEnum | string) => {
  if (isAvailableLocalStorage()) {
    localStorage.setItem('lang', language)
  }
}

const getCurrentLanguageTranslations = async() => {
  const url = createLanguageTranslationsRequestUrl(globalStore.getCurrentLanguage)
  await localizationStore.getDefaultLanguageTranslations(url)
}

const getAvailableLanguagesTranslations = () => {
  globalStore.getAvailableLanguages
    .forEach((language: LanguageEnum) => {
      if (language !== globalStore.getCurrentLanguage) {
        const url = createLanguageTranslationsRequestUrl(language)
        localizationStore.getAvailableLanguagesTranslations(url)
      }
    })
}

const setCurrentLanguage = async(isAuthorized: boolean) => {
  const languageList = Object.keys(LANGUAGE_MAP) as LanguageEnum[]
  const localStorageLanguage: LanguageEnum | string | null = isAvailableLocalStorage() ? localStorage.getItem('lang') : null

  if (isAuthorized) {
    if (
      userStore.getUserLanguage
      && languageList.some((languageKey: LanguageEnum) => languageKey === userStore.getUserLanguage)
    ) {
      globalStore.setCurrentLanguage(userStore.getUserLanguage)
    } else if (
      localStorageLanguage
      && languageList.some((languageKey: LanguageEnum) => languageKey === localStorageLanguage)
    ) {
      globalStore.setCurrentLanguage(localStorageLanguage as LanguageEnum)
      await userStore.postUserInfo({ lang: localStorageLanguage as LanguageEnum })
    } else {
      globalStore.setCurrentLanguage(LanguageEnum.en)
      await userStore.postUserInfo({ lang: LanguageEnum.en })
    }
  } else {
    const queryLanguage: string[] | undefined = getQueryByKey<string>('lang')

    if (
      queryLanguage
      && queryLanguage.length
      && languageList.some((languageKey: LanguageEnum) => languageKey === queryLanguage[0])
    ) {
      globalStore.setCurrentLanguage(queryLanguage[0] as LanguageEnum)
      setLanguageToLocalStorage(queryLanguage[0])
    } else if (
      localStorageLanguage
      && languageList.some((languageKey: LanguageEnum) => languageKey === localStorageLanguage)
    ) {
      globalStore.setCurrentLanguage(localStorageLanguage as LanguageEnum)
    } else {
      globalStore.setCurrentLanguage(LanguageEnum.en)
      setLanguageToLocalStorage(LanguageEnum.en)
    }
  }

  await getCurrentLanguageTranslations()
}

watch(() => userStore.isAuthorized, (newVal) => {
  if (newVal) {
    setCurrentLanguage(true)
  }

  if (globalStore.routeNameForAuthorizedStatusSwitching) {
    router.push({ name: globalStore.routeNameForAuthorizedStatusSwitching })
  }
}, { immediate: true })

watch(() => userStore.isCheckedAuth, (newVal) => {
  if (newVal) {
    if (!userStore.isAuthorized) {
      setCurrentLanguage(false)
    }
  }
}, { immediate: true })

watch(() => isReceivedDefaultLanguageTranslations.value, async(newVal) => {
  if (newVal) {
    await globalStore.getLanguages()
    setTimeout(() => {
      getAvailableLanguagesTranslations()
    }, 0)
  }
})

const handleResize = () => globalStore.setIsMobile(window.innerWidth)

const handleTouchStart = (e: TouchEvent) => {
  if (e.touches.length > 1) {
    e.preventDefault()
  }
}

const handleGestureStart = (e: Event) => {
  e.preventDefault()
}

onMounted(() => {
  globalStore.setIsMobile(window.innerWidth)
  document.addEventListener('touchstart', handleTouchStart, { passive: false })
  document.addEventListener('gesturestart', handleGestureStart, { passive: false })
  window.addEventListener('resize', handleResize)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
  document.removeEventListener('touchstart', handleTouchStart)
  document.removeEventListener('gesturestart', handleGestureStart)
})
</script>

<style lang="scss" module>
.container {
  width: 100%;
  height: 100dvh;
  padding: 10px;
  @include big-mobile-styles((padding: 0 10px 10px 10px));
  @include flex-column-center();
}

.content {
  @include size('100%');
}

.loader {
  height: 100%;
  @include flex-center-column();
}
</style>
